import React, { useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Eye, EyeOff, Leaf, Lock, Mail, PackageCheck, Truck } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import DecorLeaf from '../components/DecorLeaf';
import usePageMetadata from '../hooks/usePageMetadata';
import { useAuth } from '../context/AuthContext';
import { btnLg, btnPrimary, errorTextClass, focusRing, homeShell } from '../styles/ui';

// Claims already approved elsewhere on the site (Hero / BenefitsStrip). The
// reference's wording differs ("trusted farms", "100% Hygienic"); the approved
// copy wins, because those are factual sourcing and handling claims.
const HIGHLIGHTS = [
  { Icon: Leaf, title: 'Premium Quality', subtitle: 'Sourced from trusted partners' },
  { Icon: Truck, title: 'Same-Day Delivery', subtitle: 'Freshness at your doorstep' },
  { Icon: PackageCheck, title: 'Hygienically Packed', subtitle: "For your family's safety" },
];

const fieldWrap =
  'flex items-center gap-2.5 rounded-xl border border-emerald-100 bg-emerald-50/50 px-4 h-12 focus-within:border-emerald-400 focus-within:bg-white transition-colors';
const fieldInput =
  'flex-1 min-w-0 bg-transparent text-[15px] text-emerald-950 placeholder:text-ink-secondary/70 outline-none';

const LoginPage = () => {
  const { login, authStatus } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/account';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  usePageMetadata({
    title: 'Sign In | Fresh & Homely',
    description: 'Sign in to your Fresh & Homely account to shop, check out and track your orders.',
    canonicalPath: '/login',
  });

  if (authStatus === 'authenticated') {
    return <Navigate to={redirectTo} replace />;
  }

  // Unchanged: the same credential call, the same unverified-email hand-off and
  // the same redirect target (?redirect=/checkout still lands on checkout).
  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      if (err.code === 'EMAIL_NOT_VERIFIED') {
        navigate(`/verify-email?email=${encodeURIComponent(email.trim())}`);
        return;
      }
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />

      <main>
        <section className="relative overflow-hidden pt-24 sm:pt-28 pb-14 sm:pb-20 bg-gradient-to-br from-emerald-50 via-white to-emerald-50/60">
          {/* Decor only: owned inline leaves and the produce composition. */}
          <div className="pointer-events-none select-none absolute inset-0 overflow-hidden" aria-hidden="true">
            <img
              src="/hero-composition.png"
              alt=""
              className="hidden lg:block absolute right-0 xl:right-[1%] bottom-0 w-[26rem] xl:w-[32rem] 2xl:w-[36rem] object-contain"
            />
            <DecorLeaf className="hidden sm:block absolute left-6 top-28 w-16 text-emerald-500/40 -rotate-[24deg]" />
            <DecorLeaf className="hidden lg:block absolute left-[3%] bottom-24 w-20 text-emerald-600/25 rotate-[12deg]" />
            <DecorLeaf className="hidden xl:block absolute right-[38%] top-24 w-12 text-emerald-500/40 rotate-[28deg]" />
            <DecorLeaf className="hidden lg:block absolute left-[46%] bottom-10 w-10 text-emerald-600/25 rotate-[40deg]" />
            <span className="hidden xl:block absolute right-[19%] top-[18%] serif italic text-emerald-800/90 text-3xl 2xl:text-4xl leading-tight -rotate-[8deg] text-center">
              Good Food
              <br />
              Brighter Days!
            </span>
          </div>

          <div className={`${homeShell} relative`}>
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_28rem] xl:grid-cols-[minmax(0,1fr)_30rem_minmax(0,1fr)] gap-10 xl:gap-8 items-center">
              {/* ── Marketing column ─────────────────────────────────── */}
              <div className="hidden lg:block min-w-0">
                <span className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-soft rounded-full pl-3 pr-4 py-2">
                  <span className="w-7 h-7 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                    <Leaf size={15} aria-hidden="true" />
                  </span>
                  <span className="text-xs font-semibold tracking-wide text-emerald-900 uppercase">
                    Fresh, Hygienic &amp; Handpicked Daily
                  </span>
                </span>

                <h2 className="serif text-4xl xl:text-5xl 2xl:text-[3.5rem] text-emerald-950 leading-[1.08] mt-7">
                  Welcome Back
                  <br />
                  <span className="text-emerald-700">to Fresh &amp; Homely</span>
                </h2>

                <p className="text-base xl:text-lg text-ink-secondary leading-relaxed mt-5 max-w-md">
                  Sign in to continue your fresh shopping experience and enjoy quality products
                  delivered to your door.
                </p>

                <ul className="mt-9 space-y-5">
                  {HIGHLIGHTS.map(({ Icon, title, subtitle }) => (
                    <li key={title} className="flex items-center gap-4">
                      <span className="w-12 h-12 rounded-2xl bg-white shadow-soft border border-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                        <Icon size={21} aria-hidden="true" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-base font-bold text-emerald-950">{title}</span>
                        <span className="block text-sm text-ink-secondary mt-0.5">{subtitle}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* ── Sign-in card ─────────────────────────────────────── */}
              <div className="w-full max-w-md mx-auto lg:max-w-none">
                <div className="bg-white/95 backdrop-blur-sm rounded-[1.75rem] border border-emerald-50 shadow-floating p-7 sm:p-9">
                  <img
                    src="/fresh-homely-logo.png"
                    alt="Fresh &amp; Homely"
                    className="h-11 w-auto object-contain mx-auto"
                  />

                  <h1 className="serif text-3xl sm:text-[2rem] text-emerald-950 text-center mt-6">
                    Welcome back
                  </h1>
                  <p className="text-[15px] text-ink-secondary text-center mt-2">
                    Sign in to continue shopping
                  </p>

                  <form onSubmit={handleSubmit} className="mt-7 space-y-5">
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-emerald-950 mb-2">
                        Email
                      </label>
                      <div className={fieldWrap}>
                        <Mail size={18} className="text-emerald-600 shrink-0" aria-hidden="true" />
                        <input
                          id="email"
                          type="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className={fieldInput}
                          autoComplete="email"
                          placeholder="you@example.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="password" className="block text-sm font-semibold text-emerald-950 mb-2">
                        Password
                      </label>
                      <div className={fieldWrap}>
                        <Lock size={18} className="text-emerald-600 shrink-0" aria-hidden="true" />
                        <input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          required
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className={fieldInput}
                          autoComplete="current-password"
                          placeholder="Your password"
                        />
                        {/* Purely local: reveals what is already typed. */}
                        <button
                          type="button"
                          onClick={() => setShowPassword((prev) => !prev)}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          aria-pressed={showPassword}
                          className={`shrink-0 text-ink-secondary hover:text-emerald-700 rounded-md ${focusRing}`}
                        >
                          {showPassword ? (
                            <EyeOff size={18} aria-hidden="true" />
                          ) : (
                            <Eye size={18} aria-hidden="true" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* No "Remember me": sessions already persist, so the
                        control would change nothing. */}
                    <div className="flex justify-end -mt-1">
                      <Link
                        to="/forgot-password"
                        className={`text-sm font-semibold text-emerald-700 hover:text-emerald-800 ${focusRing}`}
                      >
                        Forgot your password?
                      </Link>
                    </div>

                    {error && <p className={errorTextClass}>{error}</p>}

                    <button
                      type="submit"
                      disabled={submitting}
                      className={`w-full ${btnPrimary} ${btnLg} gap-2`}
                    >
                      {submitting ? 'Signing in...' : 'Sign In'}
                      {!submitting && <ArrowRight size={18} aria-hidden="true" />}
                    </button>
                  </form>

                  {/* No Google/Apple buttons: this application has no social
                      sign-in, so they would be dead controls. */}
                  <p className="text-sm text-ink-secondary text-center mt-7">
                    New here?{' '}
                    <Link
                      to="/register"
                      className={`font-semibold text-emerald-700 hover:text-emerald-800 ${focusRing}`}
                    >
                      Create an account
                    </Link>
                  </p>
                </div>
              </div>

              {/* Spacer so the card stays optically centred at xl, where the
                  artwork occupies the third column. */}
              <div className="hidden xl:block" aria-hidden="true" />
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default LoginPage;
